import {
  resolveDomainFromSystem,
  useDomainsEntities,
} from '@agilelab/plugin-wb-builder-catalog';
import {
  WitboostDomain,
  WitboostSystem,
} from '@agilelab/plugin-wb-builder-common';
import {
  CustomViewGridElement,
  CustomViewRegister,
  EditIcon,
  GenericField,
  WbCard,
  WbButton,
  WbFieldLabel,
  useDataPath,
  useEntitiesByRefs,
} from '@agilelab/plugin-wb-platform';
import { expandEntityRef } from '@agilelab/plugin-wb-platform-common';
import { parseEntityRef } from '@backstage/catalog-model';
import { ErrorPanel } from '@backstage/core-components';
import { LinearProgress, useTheme } from '@material-ui/core';
import { useMemo } from 'react';
import { PrototypeBlueprint } from '../PrototypeBlueprint';
import { PrototypeInformationGraph } from '../PrototypeInformationGraph';
import { useSystemPrototypeDetaisContext } from '../SystemPrototypeDetails/SystemPrototypeDetaisContext';
import { PrototypeStatusBadge } from '@agilelab/plugin-wb-system-prototype-common';

CustomViewRegister.register({
  id: 'system_prototype_section_card',
  function: function SectionCard(props: Record<string, any>) {
    const { setEditedSection } = useSystemPrototypeDetaisContext();

    return (
      <WbCard
        title={props.title}
        actions={
          <WbButton
            variant="outlined"
            label="Edit"
            icon={<EditIcon />}
            onClick={() => setEditedSection(props.section)}
          />
        }
      >
        {props.children}
      </WbCard>
    );
  },
});

CustomViewRegister.register({
  id: 'system_prototype_blueprint',
  function: function Blueprint(props: Record<string, any>) {
    const ref = useDataPath(props.path || '');
    return (
      <>
        <WbFieldLabel
          label="Blueprint Identification"
          style={{ marginBottom: '4px' }}
        />
        <PrototypeBlueprint entityRef={ref} />
      </>
    );
  },
});

CustomViewRegister.register({
  id: 'system_prototype_status',
  function: function PrototypeStatus(props: Record<string, any>) {
    return (
      <CustomViewGridElement colSpan={props.colSpan}>
        <GenericField
          label={props.label}
          value={<PrototypeStatusBadge status={props.value} />}
        />
      </CustomViewGridElement>
    );
  },
});

CustomViewRegister.register({
  id: 'system_prototype_information_prototyping',
  function: function InformationPrototyping(props: Record<string, any>) {
    const data = useDataPath('');
    const businessConcepts = useDataPath(props.businessConceptsPath);
    const dataSourcesRaw = useDataPath(props.dataSourcesPath);
    const theme = useTheme();
    const HEIGHT_OFFSET = theme.spacing(2) + 104;

    const size = { width: '100%', height: `calc(100vh - ${HEIGHT_OFFSET}px)` };

    const systemRefs = useMemo(() => {
      return (dataSourcesRaw ?? []).map((ref: string) => expandEntityRef(ref));
    }, [dataSourcesRaw]);

    const {
      data: resolvedSystems,
      isLoading: resolvingSystems,
      error: resolvingSystemsError,
    } = useEntitiesByRefs(systemRefs);

    const {
      data: domains,
      isLoading: loadingDomains,
      error: domainsError,
    } = useDomainsEntities();

    const dataSources = useMemo(() => {
      if (!resolvedSystems || !domains) return [];
      return resolvedSystems.items.filter(Boolean).map(d => {
        const system = d as WitboostSystem;
        const domain = resolveDomainFromSystem(system, domains.items);
        return {
          name: system?.spec?.mesh?.name ?? system?.metadata.name,
          id: system?.metadata.name,
          domain: domain ? domain.title : system.spec.domain,
        };
      });
    }, [resolvedSystems, domains]);

    const mainDomain = useMemo(() => {
      try {
        const domain = parseEntityRef(expandEntityRef(data.domain));

        const matchingDomain = domains?.items.find(
          d =>
            d.metadata.name === domain.name &&
            d.metadata.namespace === domain.namespace,
        );
        if (!matchingDomain) return domain.name;
        return (
          (matchingDomain as WitboostDomain).spec?.mesh?.name ?? domain.name
        );
      } catch (_e) {
        return data.domain;
      }
    }, [data.domain, domains]);

    if (resolvingSystems || loadingDomains)
      return (
        <div style={size}>
          <LinearProgress />
        </div>
      );

    const error = resolvingSystemsError ?? domainsError;

    if (error)
      return (
        <div style={size}>
          <ErrorPanel
            error={
              error instanceof Error ? error : new Error('Something went wrong')
            }
          />
        </div>
      );

    return (
      <PrototypeInformationGraph
        style={size}
        prototype={{
          name: data.name,
          domain: mainDomain,
          businessConcepts,
          dataSources,
        }}
      />
    );
  },
});
